<template>
  <div class="container-xl px-4 inventory-control-page">
    <div class="row">
      <div class="col-12">
        <div class="page-header-mobile d-flex justify-content-between align-items-center mb-3 mt-2">
          <h2 class="fw-bold mb-0 page-title-mobile">Control de Inventario</h2>
        </div>
      </div>
    </div>

    <div class="mobile-context-chip d-lg-none">
      <span class="mobile-context-label">Catálogo e Inventario</span>
      <strong>Control</strong>
    </div>

    <ul class="nav nav-tabs mb-3 control-tabs">
      <li class="nav-item">
        <button class="nav-link" :class="{ active: activeTab === 'new' }" type="button" @click="activeTab = 'new'">Nuevo control</button>
      </li>
      <li class="nav-item">
        <button class="nav-link" :class="{ active: activeTab === 'load' }" type="button" @click="switchToLoad()">Cargar conteo</button>
      </li>
      <li class="nav-item">
        <button class="nav-link" :class="{ active: activeTab === 'review' }" type="button" @click="switchToReview()">Revisión/Ajustes</button>
      </li>
    </ul>

    <div v-if="activeTab === 'new'" class="row g-4">
      <div class="col-12 col-xl-6">
        <div class="card h-100">
          <div class="card-body">
            <h5 class="fw-bold">Generar Control</h5>
            <small class="text-muted d-block mb-3">Complete los datos para generar la orden de control</small>

            <div class="mb-3">
              <label class="form-label">Depósito *</label>
              <select v-if="depositSelection.requiresSelection" class="form-select" v-model.number="form.deposito_id">
                <option value="" disabled>Seleccionar depósito</option>
                <option v-for="d in depositos" :key="d.id" :value="d.id">{{ d.nombre }}</option>
              </select>
              <div v-else class="form-control bg-light">
                {{ selectedDepositoNombre || 'Depósito resuelto automáticamente' }}
              </div>
              <small class="text-muted d-block mt-1">
                {{ depositSelectionMessage }}
              </small>
              <div v-if="errors.deposito_id" class="form-text text-danger">{{ errors.deposito_id }}</div>
            </div>

            <div class="mb-3">
              <label class="form-label d-block">Categorías</label>
              <div class="border rounded p-3 bg-light categories-panel">
                <div class="row">
                  <div class="col-12 col-md-6">
                    <div class="form-check" v-for="cat in categoriasColumns[0]" :key="'l_' + cat.id">
                      <input class="form-check-input" type="checkbox" :id="'cat_' + cat.id" :value="cat.id" v-model="form.categorias" />
                      <label class="form-check-label" :for="'cat_' + cat.id">{{ cat.nombre }}</label>
                    </div>
                  </div>
                  <div class="col-12 col-md-6">
                    <div class="form-check" v-for="cat in categoriasColumns[1]" :key="'r_' + cat.id">
                      <input class="form-check-input" type="checkbox" :id="'cat_' + cat.id + '_r'" :value="cat.id" v-model="form.categorias" />
                      <label class="form-check-label" :for="'cat_' + cat.id + '_r'">{{ cat.nombre }}</label>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="errors.categorias" class="form-text text-danger">{{ errors.categorias }}</div>
            </div>

            <div class="mb-3">
              <label class="form-label">Fiscalizador *</label>
              <select class="form-select" v-model.number="form.fiscalizador_id">
                <option value="" disabled>Seleccionar fiscalizador</option>
                <option v-for="u in usuarios" :key="u.id" :value="u.id">{{ u.nombre }}</option>
              </select>
              <div v-if="errors.fiscalizador_id" class="form-text text-danger">{{ errors.fiscalizador_id }}</div>
            </div>

            <div class="mb-1">
              <label class="form-label">Controlador *</label>
              <select class="form-select" v-model.number="form.controlador_id">
                <option value="" disabled>Seleccionar controlador</option>
                <option v-for="u in usuarios" :key="'ctrl_' + u.id" :value="u.id">{{ u.nombre }}</option>
              </select>
            </div>
            <small class="text-muted d-block mb-3">Debe ser un usuario distinto al fiscalizador</small>
            <div v-if="errors.controlador_id" class="form-text text-danger mt-n2 mb-2">{{ errors.controlador_id }}</div>

            <div class="mb-3">
              <label class="form-label">Fecha de generación</label>
              <input type="text" class="form-control" :value="fechaHoyDisplay" placeholder="dd/mm/aaaa" inputmode="numeric" maxlength="10" @input="handleFechaHoyInput" />
            </div>

            <div class="mb-4">
              <label class="form-label">Observaciones</label>
              <textarea class="form-control" rows="3" placeholder="Observaciones adicionales..." v-model="form.observaciones"></textarea>
            </div>

            <div class="control-actions d-flex gap-3">
              <button class="btn btn-dark flex-grow-1" :disabled="isLoadingListado" @click="handleGenerarListado">
                <span v-if="isLoadingListado" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Generar listado
              </button>
              <button class="btn btn-link" type="button" @click="resetForm">Limpiar</button>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 col-xl-6">
        <div class="card h-100">
          <div class="card-body d-flex flex-column">
            <h5 class="fw-bold">Listado Generado</h5>
            <small class="text-muted d-block mb-3">Productos incluidos en el control</small>

            <div class="table-responsive d-none d-lg-block" v-if="items.length">
              <table class="table align-middle">
                <thead class="table-light">
                  <tr>
                    <th style="width: 40px"><input class="form-check-input" type="checkbox" v-model="selectAll" /></th>
                    <th>Producto</th>
                    <th>Categoría</th>
                    <th>Unidad base</th>
                    <th class="text-end">Stock sistema</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="it in items" :key="it.producto_id">
                    <td><input class="form-check-input" type="checkbox" v-model="selectedIds" :value="it.producto_id" /></td>
                    <td>
                      <div class="fw-semibold">{{ it.producto_nombre }}</div>
                      <small class="text-muted">{{ it.codigo_barras || it.codigo_producto }}</small>
                    </td>
                    <td>{{ it.categoria_nombre }}</td>
                    <td>{{ it.unidad_base }}</td>
                    <td class="text-end">{{ it.stock_sistema }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="items.length" class="mobile-product-checklist d-lg-none">
              <label class="mobile-select-all">
                <input class="form-check-input" type="checkbox" v-model="selectAll" />
                <span>Seleccionar todos</span>
              </label>
              <div v-for="it in items" :key="`mobile-check-${it.producto_id}`" class="mobile-item-card">
                <label class="mobile-item-check">
                  <input class="form-check-input" type="checkbox" v-model="selectedIds" :value="it.producto_id" />
                  <span class="mobile-item-title">{{ it.producto_nombre }}</span>
                </label>
                <p class="mobile-item-code">{{ it.codigo_barras || it.codigo_producto }}</p>
                <div class="mobile-item-meta">
                  <div class="mobile-meta-item">
                    <span class="mobile-meta-label">Categoría</span>
                    <strong>{{ it.categoria_nombre }}</strong>
                  </div>
                  <div class="mobile-meta-item">
                    <span class="mobile-meta-label">Unidad</span>
                    <strong>{{ it.unidad_base }}</strong>
                  </div>
                  <div class="mobile-meta-item">
                    <span class="mobile-meta-label">Stock sistema</span>
                    <strong>{{ it.stock_sistema }}</strong>
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="border rounded p-4 text-center text-muted flex-grow-1 d-flex align-items-center justify-content-center">
              <div>
                <i class="bi bi-box-seam me-2"></i>No hay productos generados todavía.
              </div>
            </div>

            <div class="control-actions d-flex gap-2 mt-3">
              <button class="btn btn-dark flex-grow-1" :disabled="!items.length || isSaving" @click="handleGuardarControl">
                <span v-if="isSaving" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Guardar control
              </button>
              <button class="btn btn-outline-secondary" type="button" @click="clearListado" :disabled="isSaving">Cancelar</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="activeTab === 'load'" class="row g-4">
      <div class="col-12">
        <div class="card mb-3">
          <div class="card-body">
            <div class="d-flex align-items-center mb-2">
              <h5 class="fw-bold mb-0 me-3">Controles</h5>
              <small class="text-muted">Selecciona un control para cargar o imprimir</small>
            </div>

            <div v-if="myControlsLoading" class="text-muted">Cargando controles...</div>
            <div v-else>
              <div v-if="!myControls.length" class="text-muted">No hay controles disponibles.</div>
              <div v-else class="list-group list-group-flush d-none d-lg-block">
                <div class="list-group-item d-flex justify-content-between align-items-center flex-wrap" v-for="ctl in myControls" :key="ctl.control_id">
                  <div class="d-flex align-items-center gap-3 flex-wrap">
                    <div class="fw-bold">Control #{{ ctl.codigo_control }}</div>
                    <span class="badge text-uppercase" :class="badgeClass(ctl.estado_control)">{{ ctl.estado_control }}</span>
                    <span class="text-muted">{{ ctl.deposito_nombre }}</span>
                    <span class="text-muted">{{ ctl.fiscalizador_nombre }}</span>
                    <span class="text-muted">{{ ctl.controlador_nombre }}</span>
                    <span class="text-muted">{{ formatDate(ctl.fecha) }}</span>
                  </div>
                  <div class="d-flex gap-2">
                    <button class="btn btn-outline-secondary btn-sm" @click="imprimirPlanilla(ctl)">Imprimir planilla</button>
                    <button class="btn btn-link btn-sm" @click="cargarPlanilla(ctl)">Cargar Planilla</button>
                  </div>
                </div>
              </div>
              <div v-if="myControls.length" class="mobile-list d-lg-none">
                <div v-for="ctl in myControls" :key="`mobile-load-${ctl.control_id}`" class="mobile-item-card">
                  <div class="mobile-item-top">
                    <strong>Control #{{ ctl.codigo_control }}</strong>
                    <span class="badge text-uppercase" :class="badgeClass(ctl.estado_control)">{{ ctl.estado_control }}</span>
                  </div>
                  <div class="mobile-item-meta">
                    <div class="mobile-meta-item">
                      <span class="mobile-meta-label">Depósito</span>
                      <strong>{{ ctl.deposito_nombre }}</strong>
                    </div>
                    <div class="mobile-meta-item">
                      <span class="mobile-meta-label">Fiscalizador</span>
                      <strong>{{ ctl.fiscalizador_nombre }}</strong>
                    </div>
                    <div class="mobile-meta-item">
                      <span class="mobile-meta-label">Controlador</span>
                      <strong>{{ ctl.controlador_nombre }}</strong>
                    </div>
                    <div class="mobile-meta-item">
                      <span class="mobile-meta-label">Fecha</span>
                      <strong>{{ formatDate(ctl.fecha) }}</strong>
                    </div>
                  </div>
                  <div class="control-actions d-flex gap-2 mt-3">
                    <button class="btn btn-outline-secondary btn-sm flex-fill" @click="imprimirPlanilla(ctl)">Imprimir planilla</button>
                    <button class="btn btn-dark btn-sm flex-fill" @click="cargarPlanilla(ctl)">Cargar Planilla</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <h5 class="fw-bold">Conteo Físico</h5>
            <small class="text-muted d-block mb-3">Ingrese las cantidades físicas contadas</small>

            <div class="mb-3 p-3 border rounded bg-light">
              <label class="form-label mb-1">Código de Barras</label>
              <input class="form-control" placeholder="Escanee o ingrese código de barras..." v-model.trim="barcodeFilter" />
              <small class="text-primary">El código debe coincidir con el producto en la tabla</small>
            </div>

            <div class="table-responsive d-none d-lg-block" v-if="filteredLoadItems.length">
              <table class="table align-middle">
                <thead class="table-light">
                  <tr>
                    <th>Producto</th>
                    <th>Unidad Medida</th>
                    <th>Categoría</th>
                    <th>Presentación</th>
                    <th>Unidades por presentación</th>
                    <th>Cantidad física</th>
                    <th>Notas</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in filteredLoadItems" :key="row.producto_id">
                    <td>
                      <div class="fw-semibold">{{ row.nombre }}</div>
                      <small class="text-muted">{{ row.codigo_barra || row.codigo_producto || '-' }}</small>
                    </td>
                    <td>{{ row.unidad_base }}</td>
                    <td>{{ row.categoria_nombre }}</td>
                    <td style="min-width: 160px;">
                      <select class="form-select form-select-sm" v-model="row.presentacionSeleccionada">
                        <option :value="null">Unidad</option>
                        <option v-for="p in row.presentaciones" :key="p._key" :value="p._key">{{ p.nombre || p.label || 'Presentación' }}</option>
                      </select>
                    </td>
                    <td>{{ unidadesPorPresentacion(row) }}</td>
                    <td style="max-width: 100px;">
                      <input type="number" min="0" class="form-control form-control-sm" v-model.number="row.cantidadFisica" />
                      <small v-if="row.presentacionSeleccionada" class="text-muted d-block mt-1">
                        Final: {{ cantidadOperativaConteo(row) }} {{ row.unidad_base }}
                      </small>
                    </td>
                    <td style="min-width: 160px;">
                      <input type="text" class="form-control form-control-sm" placeholder="Notas..." v-model.trim="row.notas" />
                    </td>
                    <td class="text-center text-muted"><i class="bi bi-exclamation-triangle"></i></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="filteredLoadItems.length" class="mobile-list d-lg-none">
              <div v-for="row in filteredLoadItems" :key="`mobile-load-item-${row.producto_id}`" class="mobile-item-card">
                <div class="mobile-item-top">
                  <div>
                    <strong>{{ row.nombre }}</strong>
                    <p class="mobile-item-code">{{ row.codigo_barra || row.codigo_producto || '-' }}</p>
                  </div>
                  <i class="bi bi-exclamation-triangle text-muted"></i>
                </div>
                <div class="mobile-item-meta">
                  <div class="mobile-meta-item">
                    <span class="mobile-meta-label">Unidad</span>
                    <strong>{{ row.unidad_base }}</strong>
                  </div>
                  <div class="mobile-meta-item">
                    <span class="mobile-meta-label">Categoría</span>
                    <strong>{{ row.categoria_nombre }}</strong>
                  </div>
                </div>
                <div class="mt-3">
                  <label class="form-label">Presentación</label>
                  <select class="form-select" v-model="row.presentacionSeleccionada">
                    <option :value="null">Unidad</option>
                    <option v-for="p in row.presentaciones" :key="p._key" :value="p._key">{{ p.nombre || p.label || 'Presentación' }}</option>
                  </select>
                </div>
                <div class="mt-3">
                  <label class="form-label">Unidades por presentación</label>
                  <div class="form-control bg-light">{{ unidadesPorPresentacion(row) }}</div>
                </div>
                <div class="mt-3">
                  <label class="form-label">Cantidad física</label>
                  <input type="number" min="0" class="form-control" v-model.number="row.cantidadFisica" />
                  <small v-if="row.presentacionSeleccionada" class="text-muted d-block mt-1">
                    Final en unidad operativa: {{ cantidadOperativaConteo(row) }} {{ row.unidad_base }}
                  </small>
                </div>
                <div class="mt-3">
                  <label class="form-label">Notas</label>
                  <input type="text" class="form-control" placeholder="Notas..." v-model.trim="row.notas" />
                </div>
              </div>
            </div>

            <div v-else class="text-muted">No hay items para cargar. Seleccione un control y pulse "Cargar Planilla".</div>

            <div class="d-flex justify-content-end mt-3">
              <button class="btn btn-dark" :disabled="!loadItems.length || savingLoad" @click="finalizarCarga">
                <span v-if="savingLoad" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Finalizar carga
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="activeTab === 'review'" class="row g-4">
      <div class="col-12">
        <div class="card mb-3">
          <div class="card-body">
            <div class="d-flex align-items-center mb-2">
              <h5 class="fw-bold mb-0 me-3">Controles</h5>
              <small class="text-muted">Selecciona un control para revisar</small>
            </div>

            <div v-if="reviewControlsLoading" class="text-muted">Cargando controles...</div>
            <div v-else>
              <div v-if="!reviewControls.length" class="text-muted">No hay controles disponibles.</div>
              <div v-else class="list-group list-group-flush d-none d-lg-block">
                <div class="list-group-item d-flex justify-content-between align-items-center flex-wrap" v-for="ctl in reviewControls" :key="'r_' + ctl.control_id">
                  <div class="d-flex align-items-center gap-3 flex-wrap">
                    <div class="fw-bold">Control #{{ ctl.codigo_control }}</div>
                    <span class="badge text-uppercase" :class="badgeClass(ctl.estado_control)">{{ ctl.estado_control }}</span>
                    <span class="text-muted">{{ ctl.deposito_nombre }}</span>
                    <span class="text-muted">{{ formatDate(ctl.fecha) }}</span>
                  </div>
                  <div class="d-flex gap-2">
                    <button class="btn btn-link btn-sm" @click="revisarPlanilla(ctl)">Revisar Planilla</button>
                  </div>
                </div>
              </div>
              <div v-if="reviewControls.length" class="mobile-list d-lg-none">
                <div v-for="ctl in reviewControls" :key="`mobile-review-${ctl.control_id}`" class="mobile-item-card">
                  <div class="mobile-item-top">
                    <strong>Control #{{ ctl.codigo_control }}</strong>
                    <span class="badge text-uppercase" :class="badgeClass(ctl.estado_control)">{{ ctl.estado_control }}</span>
                  </div>
                  <div class="mobile-item-meta">
                    <div class="mobile-meta-item">
                      <span class="mobile-meta-label">Depósito</span>
                      <strong>{{ ctl.deposito_nombre }}</strong>
                    </div>
                    <div class="mobile-meta-item">
                      <span class="mobile-meta-label">Fecha</span>
                      <strong>{{ formatDate(ctl.fecha) }}</strong>
                    </div>
                  </div>
                  <div class="control-actions d-flex gap-2 mt-3">
                    <button class="btn btn-dark btn-sm flex-fill" @click="revisarPlanilla(ctl)">Revisar Planilla</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <h5 class="fw-bold">Revisión de Diferencias</h5>
            <small class="text-muted d-block mb-3">Revise las diferencias y tome decisiones por cada item</small>

            <div class="table-responsive d-none d-lg-block" v-if="reviewItems.length">
              <table class="table align-middle">
                <thead class="table-light">
                  <tr>
                    <th>Producto</th>
                    <th>U. base</th>
                    <th>Sistema</th>
                    <th>Físico</th>
                    <th>Diferencia</th>
                    <th>Decisión</th>
                    <th>Vencimiento</th>
                    <th>Motivo</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in reviewItems" :key="row.producto_id">
                    <td>
                      <div class="fw-semibold">{{ row.nombre }}</div>
                      <small class="text-muted">{{ row.codigo_barra || '-' }}</small>
                    </td>
                    <td>{{ row.unidad_base }}</td>
                    <td>{{ row.sistema }}</td>
                    <td>{{ row.fisico }}</td>
                    <td>
                      <span :class="['badge', diffBadgeClass(row.diferencia)]">{{ signed(row.diferencia) }}</span>
                    </td>
                    <td style="min-width: 160px;">
                      <select class="form-select form-select-sm" v-model="row.decision" :disabled="!puedeRevisar">
                        <option value="PENDIENTE">PENDIENTE</option>
                        <option value="ACEPTAR">ACEPTAR</option>
                        <option value="RECHAZAR">RECHAZAR</option>
                        <option value="AJUSTAR">AJUSTAR</option>
                      </select>
                    </td>
                    <td style="min-width: 160px;">
                      <input type="date" class="form-control form-control-sm" :value="earliestIngreso(row.vencimientos)" disabled />
                    </td>
                    <td style="min-width: 160px;">
                      <input type="text" class="form-control form-control-sm" placeholder="Motivo..." v-model.trim="row.motivo" />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="reviewItems.length" class="mobile-list d-lg-none">
              <div v-for="row in reviewItems" :key="`mobile-review-item-${row.producto_id}`" class="mobile-item-card">
                <div class="mobile-item-top">
                  <div>
                    <strong>{{ row.nombre }}</strong>
                    <p class="mobile-item-code">{{ row.codigo_barra || '-' }}</p>
                  </div>
                  <span :class="['badge', diffBadgeClass(row.diferencia)]">{{ signed(row.diferencia) }}</span>
                </div>
                <div class="mobile-item-meta">
                  <div class="mobile-meta-item">
                    <span class="mobile-meta-label">Unidad</span>
                    <strong>{{ row.unidad_base }}</strong>
                  </div>
                  <div class="mobile-meta-item">
                    <span class="mobile-meta-label">Sistema</span>
                    <strong>{{ row.sistema }}</strong>
                  </div>
                  <div class="mobile-meta-item">
                    <span class="mobile-meta-label">Físico</span>
                    <strong>{{ row.fisico }}</strong>
                  </div>
                  <div class="mobile-meta-item">
                    <span class="mobile-meta-label">Vencimiento</span>
                    <strong>{{ earliestIngreso(row.vencimientos) || '-' }}</strong>
                  </div>
                </div>
                <div class="mt-3">
                  <label class="form-label">Decisión</label>
                  <select class="form-select" v-model="row.decision" :disabled="!puedeRevisar">
                    <option value="PENDIENTE">PENDIENTE</option>
                    <option value="ACEPTAR">ACEPTAR</option>
                    <option value="RECHAZAR">RECHAZAR</option>
                    <option value="AJUSTAR">AJUSTAR</option>
                  </select>
                </div>
                <div class="mt-3">
                  <label class="form-label">Motivo</label>
                  <input type="text" class="form-control" placeholder="Motivo..." v-model.trim="row.motivo" :disabled="!puedeRevisar" />
                </div>
              </div>
            </div>

            <div v-else class="text-muted">Selecciona un control y pulsa "Revisar Planilla".</div>

            <div class="control-footer-actions d-flex gap-2 mt-3">
              <button v-if="puedeRevisar" class="btn btn-outline-secondary btn-sm" :disabled="!reviewItems.length || operacionEnCurso" @click="marcarTodos('ACEPTAR')">Marcar todos con diferencia = ACEPTAR AJUSTE</button>
              <button v-if="puedeRevisar" class="btn btn-outline-secondary btn-sm" :disabled="!reviewItems.length || operacionEnCurso" @click="marcarTodos('RECHAZAR')">Marcar todos = RECHAZAR</button>
              <div class="ms-auto d-flex gap-2 footer-primary-actions">
                <!-- RF-11: "Ejecutar ajustes" suelto ya no existe. El backend lee
                     las decisiones de la base, así que ejecutar sin guardar antes
                     no ajustaba nada y aun así cerraba el control como AJUSTADO. -->
                <button v-if="puedeRevisar" class="btn btn-warning" :disabled="!canSaveReview || operacionEnCurso" @click="guardarRevision">
                  <span v-if="savingReview" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Guardar
                </button>
                <button v-if="puedeAjustar" class="btn btn-success" :disabled="!canExecuteAdjustments || operacionEnCurso" @click="guardarYEjecutar">
                  <span v-if="executingAdj" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Guardar y ejecutar
                </button>
              </div>
            </div>
            <p v-if="!puedeRevisar" class="text-muted small mt-2 mb-0">
              Tu perfil puede consultar esta planilla, pero no registrar decisiones ni ejecutar ajustes.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import InventoryControlService from '@/services/InventoryControlService'
import apiService from '@/services/apiService'
import operationalConfigService from '@/services/operationalConfigService'
import { applyDateMask, toApiDate, toDisplayDate, todayApi } from '@/utils/date'
import { hasPermission } from '@/services/authSessionService'

const activeTab = ref('new')

const form = reactive({
  deposito_id: '',
  categorias: [],
  fiscalizador_id: '',
  controlador_id: '',
  observaciones: ''
})

const currentSucursalId = operationalConfigService.getCurrentSucursalId()
const depositos = ref([])
const operationalConfig = ref(operationalConfigService.getConfig(currentSucursalId))

const categoriasOptions = ref([])
const usuarios = ref([])

const isLoadingOptions = reactive({ categorias: false, usuarios: false })

const fechaHoy = ref(todayApi())
const fechaHoyDisplay = computed(() => toDisplayDate(fechaHoy.value))
const depositSelection = computed(() =>
  operationalConfigService.resolveDestinationWarehouse({
    purpose: 'entrada',
    sucursalId: currentSucursalId,
    warehouses: depositos.value
  })
)
const selectedDepositoNombre = computed(() =>
  depositos.value.find(dep => dep.id === Number(form.deposito_id))?.nombre || ''
)
const depositSelectionMessage = computed(() => {
  if (depositSelection.value.reason === 'mostrador_unico') {
    return 'La sucursal opera en modo monodepósito: el sistema usará automáticamente el depósito mostrador.'
  }
  if (depositSelection.value.reason === 'default_entrada') {
    return 'La sucursal tiene un depósito default de entrada configurado.'
  }
  return 'Seleccione manualmente el depósito destino del control.'
})

function handleFechaHoyInput(event) {
  const masked = applyDateMask(event.target.value)
  event.target.value = masked
  fechaHoy.value = toApiDate(masked) || (masked ? fechaHoy.value : '')
}

function normalizeWarehouse(raw) {
  return {
    id: raw.deposito_id,
    sucursalId: raw.sucursal_id,
    nombre: raw.nombre,
    activo: !!raw.activo,
    habilitado: !!raw.habilitado
  }
}

function applyResolvedDeposit() {
  operationalConfig.value = operationalConfigService.getConfig(currentSucursalId)
  const resolved = operationalConfigService.resolveDestinationWarehouse({
    purpose: 'entrada',
    sucursalId: currentSucursalId,
    warehouses: depositos.value
  })

  if (!resolved.requiresSelection && resolved.depositoId) {
    form.deposito_id = resolved.depositoId
    errors.deposito_id = ''
  } else if (!depositos.value.some(dep => dep.id === Number(form.deposito_id))) {
    form.deposito_id = ''
  }
}

const items = ref([])
const header = ref(null)

const categoriasColumns = computed(() => {
  const half = Math.ceil(categoriasOptions.value.length / 2)
  return [
    categoriasOptions.value.slice(0, half),
    categoriasOptions.value.slice(half)
  ]
})

const selectedIds = ref([])
const selectAll = computed({
  get() {
    return items.value.length && selectedIds.value.length === items.value.length
  },
  set(val) {
    selectedIds.value = val ? items.value.map(i => i.producto_id) : []
  }
})

const isLoadingListado = ref(false)
const isSaving = ref(false)

const errors = reactive({})
function validateForm(forSave = false) {
  errors.deposito_id = !form.deposito_id ? 'Seleccione un depósito' : ''
  errors.categorias = !form.categorias.length ? 'Seleccione al menos una categoría' : ''
  if (forSave) {
    errors.fiscalizador_id = !form.fiscalizador_id ? 'Seleccione un fiscalizador' : ''
    errors.controlador_id = !form.controlador_id ? 'Seleccione un controlador' : ''
    if (form.fiscalizador_id && form.controlador_id && form.fiscalizador_id === form.controlador_id) {
      errors.controlador_id = 'El controlador debe ser distinto al fiscalizador'
    }
  } else {
    errors.fiscalizador_id = ''
    errors.controlador_id = ''
  }
  return !Object.values(errors).some(Boolean)
}

async function handleGenerarListado() {
  if (!validateForm(false)) return
  isLoadingListado.value = true
  try {
    const payload = { deposito_id: Number(form.deposito_id), categorias: form.categorias.map(Number) }
    const { data } = await InventoryControlService.getPreliminaryList(payload)
    header.value = data.header
    items.value = Array.isArray(data.items) ? data.items : []
    selectedIds.value = items.value.map(i => i.producto_id)
  } catch (e) {
    console.error('Error generando listado preliminar de control:', e)
    window.alert('No se pudo generar el listado preliminar del control.')
  } finally {
    isLoadingListado.value = false
  }
}

async function handleGuardarControl() {
  if (!validateForm(true)) return
  if (!items.value.length) return
  isSaving.value = true
  try {
    const payload = {
      deposito_id: Number(form.deposito_id),
      categorias: form.categorias.map(Number),
      fiscalizador_id: Number(form.fiscalizador_id),
      controlador_id: Number(form.controlador_id),
      observaciones: form.observaciones?.trim() || ''
    }
    const { data } = await InventoryControlService.createControl(payload)
    window.alert(data?.message || 'Control generado correctamente')
    resetForm()
    clearListado()
    activeTab.value = 'load'
    await fetchMyControls()
  } catch (e) {
    console.error('Error guardando control de inventario:', e)
    window.alert('No se pudo guardar el control de inventario.')
  } finally {
    isSaving.value = false
  }
}

function resetForm() {
  form.deposito_id = ''
  form.categorias = []
  form.fiscalizador_id = ''
  form.controlador_id = ''
  form.observaciones = ''
  applyResolvedDeposit()
}

function clearListado() {
  items.value = []
  header.value = null
  selectedIds.value = []
}

watch(() => [form.deposito_id, form.categorias.slice()], clearListado)

onMounted(async () => {
  try {
    const { data } = await apiService.get('/api/inventory/warehouses')
    const rows = Array.isArray(data?.depositos) ? data.depositos : []
    depositos.value = rows
      .map(normalizeWarehouse)
      .filter(dep => Number(dep.sucursalId) === Number(currentSucursalId))
    applyResolvedDeposit()
  } catch (e) {
    depositos.value = []
  }

  try {
    isLoadingOptions.categorias = true
    const { data } = await InventoryControlService.getCategories({ page: 1, limit: 100 })
    const rows = data?.data?.data || []
    categoriasOptions.value = rows.map(c => ({ id: c.categoria_id, nombre: c.nombre }))
  } catch (e) {
    console.error('Error cargando categorías para control de inventario:', e)
    categoriasOptions.value = []
  } finally {
    isLoadingOptions.categorias = false
  }

  try {
    isLoadingOptions.usuarios = true
    const { data } = await InventoryControlService.getUsers()
    const rows = data?.data || []
    usuarios.value = Array.isArray(rows) ? rows.map(u => ({ id: u.usuario_id, nombre: u.nombre })) : []
  } catch (e) {
    console.error('Error cargando usuarios para control de inventario:', e)
    usuarios.value = []
  } finally {
    isLoadingOptions.usuarios = false
  }
})

watch(depositSelection, () => {
  applyResolvedDeposit()
})

const myControls = ref([])
const myControlsLoading = ref(false)
const selectedControl = ref(null)
const loadHeader = ref(null)
const loadItems = ref([])
const savingLoad = ref(false)
const barcodeFilter = ref('')

function switchToLoad() {
  activeTab.value = 'load'
  if (!myControls.value.length) fetchMyControls()
}

async function fetchMyControls() {
  myControlsLoading.value = true
  try {
    const { data } = await InventoryControlService.getMyControls()
    myControls.value = data?.controls || []
    if (!Array.isArray(myControls.value)) myControls.value = []
  } catch (e) {
    const now = new Date()
    const paraguayDate = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Asuncion',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(now)
    myControls.value = [
      {
        control_id: 1,
        codigo_control: 'CTRL-001',
        estado_control: 'GENERADO',
        fecha: paraguayDate + 'T00:00:00.000Z',
        deposito_id: 1,
        deposito_nombre: 'Depósito Central',
        fiscalizador_id: 1,
        controlador_id: 1,
        fiscalizador_nombre: 'Emilio',
        controlador_nombre: 'Emilio'
      }
    ]
  } finally {
    myControlsLoading.value = false
  }
}

async function cargarPlanilla(ctl) {
  selectedControl.value = ctl
  try {
    const { data } = await InventoryControlService.getControlLoadDetail(ctl.control_id)
    loadHeader.value = data?.header || null
    const items = Array.isArray(data?.items) ? data.items : []
    loadItems.value = items.map((it, idx) => ({
      ...it,
      presentaciones: (it.presentaciones || []).map((p, i) => ({ ...p, _key: `${idx}_${i}` })),
      presentacionSeleccionada: null,
      cantidadFisica: 0,
      notas: ''
    }))
  } catch (e) {
    loadHeader.value = {
      control_id: ctl?.control_id ?? 1,
      codigo_control: ctl?.codigo_control ?? 'CTRL-001',
      estado_control: ctl?.estado_control ?? 'GENERADO',
      deposito_id: ctl?.deposito_id ?? 1,
      deposito_nombre: ctl?.deposito_nombre ?? 'Depósito Central',
      fiscalizador_id: ctl?.fiscalizador_id ?? 1,
      fiscalizador_nombre: ctl?.fiscalizador_nombre ?? 'Emilio',
      controlador_id: ctl?.controlador_id ?? 1,
      controlador_nombre: ctl?.controlador_nombre ?? 'Emilio',
      fecha: ctl?.fecha ?? (new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Asuncion',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).format(new Date()) + 'T00:00:00.000Z')
    }
    const fallbackItems = [
      {
        producto_id: 33,
        producto_nombre: 'Prueba004',
        unidad_base: 'onza',
        categoria_nombre: 'Embutidos',
        presentaciones: []
      }
    ]
    loadItems.value = fallbackItems.map((it) => ({
      ...it,
      presentaciones: [],
      presentacionSeleccionada: null,
      cantidadFisica: 0,
      notas: ''
    }))
  }
}

function imprimirPlanilla(ctl) {
  InventoryControlService.getControlLoadDetail(ctl.control_id)
    .then(({ data }) => buildPrintWindow(data?.header, data?.items))
    .catch(() => {
      const fallbackHeader = {
        codigo_control: ctl?.codigo_control ?? 'CTRL-XXX',
        deposito_nombre: ctl?.deposito_nombre ?? '',
        fiscalizador_nombre: ctl?.fiscalizador_nombre ?? '',
        controlador_nombre: ctl?.controlador_nombre ?? '',
        fecha: ctl?.fecha ?? (new Intl.DateTimeFormat('en-CA', {
          timeZone: 'America/Asuncion',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        }).format(new Date()) + 'T00:00:00.000Z')
      }
      const fallbackItems = [
        { producto_nombre: 'Producto demo', unidad_base: 'unidad', categoria_nombre: 'Categoría', codigo_barras: '' }
      ]
      buildPrintWindow(fallbackHeader, fallbackItems)
    })

  function buildPrintWindow(header, rawItems) {
    const win = window.open('', '_blank')
    if (!win) return

    const escape = (str) => String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')

    const items = Array.isArray(rawItems) ? rawItems : []
    const rows = items.map(i => {
      const nombre = i.producto_nombre || i.nombre || i.nombre_producto || i.descripcion || ''
      const unidad = i.unidad_base || i.unidad_medida || ''
      const categoria = i.categoria_nombre || i.categoria || ''
      return `<tr>
        <td>${escape(nombre)}</td>
        <td>${escape(unidad)}</td>
        <td>${escape(categoria)}</td>
        <td></td>
      </tr>`
    }).join('')

    const html = `<!DOCTYPE html><html><head><title>Planilla ${escape(header?.codigo_control || '')}</title>
      <meta charset="utf-8" />
      <style>
        body{font-family:Arial,Helvetica,sans-serif;padding:24px;font-size:13px;color:#111}
        h3{margin:0 0 8px;font-size:18px}
        .meta{margin-bottom:12px}
        table{width:100%;border-collapse:collapse;margin-top:12px}
        th,td{border:1px solid #555;padding:6px 8px;vertical-align:top;font-size:12px}
        th{background:#f2f2f2;text-align:left}
        td:last-child{width:120px}
        @media print { body{padding:8px} }
      </style>
    </head><body>
      <h3>Planilla de Conteo - ${escape(header?.codigo_control || '')}</h3>
      <div class="meta">
        <strong>Depósito:</strong> ${escape(header?.deposito_nombre || '')}<br/>
        <strong>Fiscalizador:</strong> ${escape(header?.fiscalizador_nombre || '')} &nbsp; 
        <strong>Controlador:</strong> ${escape(header?.controlador_nombre || '')}<br/>
        <strong>Fecha:</strong> ${escape(formatDate(header?.fecha))}
      </div>
      <table>
        <thead><tr><th>Producto</th><th>Unidad</th><th>Categoría</th><th>Cantidad</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <script>window.onload=()=>window.print()</scr${''}ipt>
    </body></html>`
    win.document.open()
    win.document.write(html)
    win.document.close()
  }
}

const filteredLoadItems = computed(() => {
  const q = barcodeFilter.value.trim().toLowerCase()
  if (!q) return loadItems.value
  return loadItems.value.filter(r =>
    String(r.codigo_barras || r.codigo_producto || '').toLowerCase().includes(q)
  )
})

function unidadesPorPresentacion(row) {
  const sel = row.presentaciones?.find(p => p._key === row.presentacionSeleccionada)
  return sel?.unidades_por_presentacion ? `${sel.unidades_por_presentacion} unidades` : '1'
}

function selectedPresentation(row) {
  return row.presentaciones?.find(p => p._key === row.presentacionSeleccionada) || null
}

function cantidadOperativaConteo(row) {
  const cantidad = Number(row.cantidadFisica) || 0
  const presentation = selectedPresentation(row)
  const factor = Number(presentation?.unidades_por_presentacion) || 1
  return Math.trunc(cantidad * factor)
}

async function finalizarCarga() {
  if (!selectedControl.value || !loadItems.value.length) return
  const itemsPayload = loadItems.value.map(it => {
    const presentation = selectedPresentation(it)
    if (presentation?.presentacion_id) {
      return {
        producto_id: it.producto_id,
        presentacion_id: Number(presentation.presentacion_id),
        cantidad_presentaciones: Number(it.cantidadFisica) || 0,
        unidades_sueltas: 0
      }
    }
    return {
      producto_id: it.producto_id,
      cantidad_fisica: Number(it.cantidadFisica) || 0
    }
  })

  const hasAny = itemsPayload.some(i => (i.cantidad_fisica || i.cantidad_presentaciones || 0) > 0)
  if (!hasAny) {
    window.alert('Ingrese al menos una cantidad física antes de finalizar.')
    return
  }

  savingLoad.value = true
  try {
    await InventoryControlService.updateControlLoad(selectedControl.value.control_id, { items: itemsPayload })
    window.alert('Conteo físico guardado correctamente.')
    activeTab.value = 'review'
    fetchReviewControls()
  } catch (e) {
    console.error('Error guardando conteo físico:', e)
    window.alert('Error al guardar el conteo físico. Revise la consola.')
  } finally {
    savingLoad.value = false
  }
}

function formatDate(dateStr) {
  if (!dateStr) return ''
  try {
    const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (match) {
      const [, y, m, d] = match
      return `${d}/${m}/${y}`
    }
    return dateStr
  } catch (error) {
    console.warn('Error formateando fecha:', dateStr, error)
    return dateStr
  }
}

function badgeClass(estado) {
  const e = String(estado || '').toUpperCase()
  if (e.includes('CANCEL')) return 'bg-danger'
  if (e.includes('CARG')) return 'bg-dark text-white'
  if (e.includes('PEND')) return 'bg-warning'
  return 'bg-success'
}

const reviewControls = ref([])
const reviewControlsLoading = ref(false)
const reviewHeader = ref(null)
const reviewItems = ref([])
const savingReview = ref(false)
const executingAdj = ref(false)

function switchToReview() {
  activeTab.value = 'review'
  if (!reviewControls.value.length) fetchReviewControls()
}

async function fetchReviewControls() {
  reviewControlsLoading.value = true
  try {
    const { data } = await InventoryControlService.getMyFiscalizations()
    reviewControls.value = data?.controls || []
    if (!Array.isArray(reviewControls.value)) reviewControls.value = []
  } catch (e) {
    reviewControls.value = []
  } finally {
    reviewControlsLoading.value = false
  }
}

async function revisarPlanilla(ctl) {
  try {
    const { data } = await InventoryControlService.getRevisionData(ctl.control_id)
    reviewHeader.value = data?.header || null
    const items = Array.isArray(data?.items) ? data.items : []
    reviewItems.value = items.map(it => ({
      ...it,
      decision: it.decision || 'PENDIENTE',
      motivo: it.motivo || ''
    }))
  } catch (e) {
    reviewHeader.value = null
    reviewItems.value = []
  }
}

function earliestIngreso(vencs) {
  const arr = Array.isArray(vencs) ? vencs : []
  if (!arr.length) return ''
  const min = arr.reduce((acc, v) => {
    const current = new Date(v.ingreso)
    return !acc || current < acc ? current : acc
  }, null)
  if (!min) return ''
  try {
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Asuncion',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
    return formatter.format(min)
  } catch {
    return ''
  }
}

function diffBadgeClass(diff) {
  if (diff > 0) return 'bg-success'
  if (diff < 0) return 'bg-danger'
  return 'bg-secondary'
}

function signed(n) {
  if (n > 0) return `+${n}`
  return String(n)
}

function marcarTodos(tipo) {
  if (!reviewItems.value.length) return
  reviewItems.value = reviewItems.value.map(it => ({
    ...it,
    decision: tipo
  }))
}

// El backend exige estos permisos en PUT /:id/review y
// POST /:id/execute-adjustments (inventoryControlRoutes.js). El guard de la
// ruta pasa con `control_inventario_cargar` solo, así que sin este gateo el
// ayudante de depósito veía los botones y se comía dos 403 (§12.6 del SPEC).
const puedeRevisar = computed(() => hasPermission('control_inventario_revisar'))
const puedeAjustar = computed(() => puedeRevisar.value && hasPermission('control_inventario_ajustar'))

const canSaveReview = computed(() => !!(reviewHeader.value && reviewItems.value.length) && puedeRevisar.value)
const canExecuteAdjustments = computed(() => !!(reviewHeader.value && reviewItems.value.length) && puedeAjustar.value)

// Evita el doble envío: sobre execute-adjustments significaría intentar
// ajustar dos veces el mismo control.
const operacionEnCurso = computed(() => savingReview.value || executingAdj.value)

// Ítems con diferencia real que el supervisor todavía no decidió. El backend
// los ignora (`decision in ('ACEPTAR','AJUSTAR')`) pero cierra el control
// igual, así que hay que avisarlo antes y no después.
const itemsSinDecision = computed(() =>
  reviewItems.value.filter((it) => Number(it.diferencia) !== 0 && String(it.decision || 'PENDIENTE').toUpperCase() === 'PENDIENTE')
)

function mensajeErrorControl(e, fallback) {
  return e?.userMessage
    || e?.response?.data?.error
    || e?.response?.data?.message
    || fallback
}

// Persiste las decisiones. Devuelve el payload del backend, o `null` si falló:
// quien la llama necesita distinguir esos dos casos para decidir si sigue.
async function persistirRevision() {
  const decisiones = reviewItems.value.map(it => ({
    producto_id: it.producto_id,
    decision: it.decision,
    motivo: it.motivo || ''
  }))
  const { data } = await InventoryControlService.reviewControl(reviewHeader.value.control_id, decisiones)
  return data
}

async function guardarRevision() {
  if (!canSaveReview.value || operacionEnCurso.value) return
  savingReview.value = true
  try {
    const data = await persistirRevision()
    const aplicadas = data?.decisiones_aplicadas ?? data?.control?.decisiones_aplicadas
    window.alert.success(
      Number.isFinite(Number(aplicadas))
        ? `Revisión guardada. ${aplicadas} decisiones registradas.`
        : 'Revisión guardada.',
      { title: 'Control de inventario' }
    )
    fetchReviewControls()
  } catch (e) {
    console.error('Error guardando revisión de control:', e)
    window.alert.error(mensajeErrorControl(e, 'No se pudo guardar la revisión del control.'), { title: 'Control de inventario' })
  } finally {
    savingReview.value = false
  }
}

// Guarda y, SOLO si el guardado salió bien, ejecuta. Ejecutar sobre decisiones
// no persistidas es exactamente el bug que esta tarea corrige.
async function guardarYEjecutar() {
  if (!canExecuteAdjustments.value || operacionEnCurso.value) return

  const pendientes = itemsSinDecision.value.length
  if (pendientes > 0) {
    const confirmado = await window.confirm(
      `Hay ${pendientes} ítem(s) con diferencia y sin decisión. Esos ítems no se van a ajustar y el control quedará cerrado igual.`,
      { title: 'Ejecutar ajustes', confirmText: 'Ejecutar', cancelText: 'Volver a revisar' }
    )
    // "Volver a revisar" cancela todo: no guarda y no ejecuta.
    if (!confirmado) return
  }

  executingAdj.value = true
  let revisionGuardada = false
  try {
    await persistirRevision()
    revisionGuardada = true

    const { data } = await InventoryControlService.executeAdjustments(reviewHeader.value.control_id)
    const positivos = data?.summary?.positivos ?? 0
    const negativos = data?.summary?.negativos ?? 0
    if (Number(positivos) === 0 && Number(negativos) === 0) {
      window.alert.info('Revisión guardada. No había ítems con diferencia para ajustar; el control quedó cerrado.', { title: 'Control de inventario' })
    } else {
      window.alert.success(`Ajustes ejecutados: ${positivos} sobrantes y ${negativos} faltantes. Control cerrado.`, { title: 'Control de inventario' })
    }
    fetchReviewControls()
    reviewItems.value = []
    reviewHeader.value = null
  } catch (e) {
    console.error('Error en guardar y ejecutar del control:', e)
    if (revisionGuardada) {
      // El caso que hoy no tiene mensaje y es el que más importa: el
      // supervisor tiene que saber que su trabajo de revisión no se perdió.
      window.alert.error(
        `La revisión se guardó, pero los ajustes no se pudieron ejecutar: ${mensajeErrorControl(e, 'error desconocido')}. El control sigue abierto y podés reintentar.`,
        { title: 'Control de inventario' }
      )
      fetchReviewControls()
    } else {
      window.alert.error(mensajeErrorControl(e, 'No se pudo guardar la revisión del control.'), { title: 'Control de inventario' })
    }
  } finally {
    executingAdj.value = false
  }
}
</script>

<style scoped>
.inventory-control-page {
  max-width: 1450px;
}

.page-title-mobile {
  font-size: clamp(1.35rem, 2vw, 1.8rem);
}

.mobile-context-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 1rem;
  padding: 0.45rem 0.8rem;
  border-radius: 999px;
  background: #f3f4f6;
  color: #111827;
  font-size: 0.82rem;
}

.mobile-context-label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
}

.control-tabs .nav-link {
  color: #334155;
  font-weight: 600;
}

.control-tabs .nav-link.active {
  color: #111827;
}

.categories-panel {
  max-height: 280px;
  overflow-y: auto;
}

.mobile-list,
.mobile-product-checklist {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.mobile-item-card {
  padding: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  box-shadow: 0 16px 34px rgba(15, 23, 42, 0.06);
}

.mobile-item-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.85rem;
  margin-bottom: 0.85rem;
}

.mobile-item-check {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  font-weight: 700;
}

.mobile-item-title {
  color: #0f172a;
}

.mobile-item-code {
  margin: 0.35rem 0 0;
  font-size: 0.82rem;
  color: #64748b;
}

.mobile-item-meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.65rem;
}

.mobile-meta-item {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 0.75rem 0.85rem;
  border-radius: 0.85rem;
  background: #fff;
  border: 1px solid #e5e7eb;
}

.mobile-meta-label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
}

.mobile-select-all {
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  margin-bottom: 0.25rem;
  font-weight: 600;
}

.control-actions .btn,
.footer-primary-actions .btn {
  white-space: nowrap;
}

@media (max-width: 991.98px) {
  .page-header-mobile {
    align-items: stretch !important;
    flex-direction: column;
    gap: 0.85rem;
  }

  .control-tabs {
    gap: 0.5rem;
    border-bottom: 0;
  }

  .control-tabs .nav-item {
    width: 100%;
  }

  .control-tabs .nav-link {
    width: 100%;
    border: 1px solid #d1d5db;
    border-radius: 0.85rem;
    background: #fff;
  }

  .control-tabs .nav-link.active {
    border-color: #111827;
    background: #111827;
    color: #fff;
  }

  .control-actions,
  .control-footer-actions,
  .footer-primary-actions {
    flex-direction: column;
  }
}

@media (max-width: 575.98px) {
  .inventory-control-page {
    padding-left: 1rem !important;
    padding-right: 1rem !important;
  }

  .mobile-item-meta {
    grid-template-columns: 1fr;
  }

  .mobile-item-top {
    flex-direction: column;
  }
}
</style>
